<template>
  <section class="section section--slider section--home">
    <div class="container-lg">
      <div class="mb-6 md:mb-8 row align-items-center">
        <div class="col-lg-9">
          <!-- Title -->
          <template v-if="loading">
            <div class="skeleton-text skeleton-title w-1/3 mb-2"></div>
          </template>
          <template v-else>
            <h2 class="section__title">{{ in18.page_popular_slider_title }}</h2>
          </template>

          <!-- Categories -->
          <div class="section__header-list-wrapper">
            <ul class="section__header-list" v-if="loading">
              <li v-for="n in 4" :key="n">
                <div class="skeleton-text skeleton-link w-24 h-4"></div>
              </li>
            </ul>
            <ul class="section__header-list" v-else>
              <li v-for="category in featured_categories" :key="category.id">
                <a :href="category.link" :title="category.title">
                  {{ category.title }}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <!-- Custom arrows -->
        <div class="col-lg-3 text-lg-end">
          <div class="slider-arrows slider-arrows--tobottom">
            <button
                class="slider__arrows-prev"
                :title="in18.page_previous"
                type="button"
            >
              {{ in18.page_previous }}
            </button>
            <button
                class="slider__arrows-next"
                :title="in18.page_next"
                type="button"
            >
              {{ in18.page_next }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="container-lg">
      <!-- Loading skeleton -->
      <div v-if="loading" class="my-swiper flex gap-4 overflow-hidden">
        <div
            v-for="n in placeholderCount"
            :key="n"
            class="skeleton-item rounded-lg flex-1 h-48"
        ></div>
      </div>

      <!-- Real Swiper -->
      <swiper
          v-else
          ref="swiperRef"
          loop
          lazy
          :speed="600"
          :modules="[Autoplay, Navigation]"
          :autoplay="{ delay: 2500, disableOnInteraction: false, pauseOnMouseEnter: true }"
          :slides-per-view="slidesPerView"
          :breakpoints="breakpoints"
          class="products-slider"
          :navigation="{
          prevEl: '.slider__arrows-prev',
          nextEl: '.slider__arrows-next'
        }"
      >
        <swiper-slide
            v-for="product in products"
            :key="product.id"
            class="slider-item pt-3"
        >
          <product-item :product="product"/>
        </swiper-slide>
      </swiper>
    </div>
  </section>
</template>

<script setup>
import {ref, onMounted, onUnmounted, computed} from 'vue'
import {Swiper, SwiperSlide} from 'swiper/vue'
import {Autoplay, Navigation} from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import axios from 'axios'

const props = defineProps({
  type:                String,
  in18:                Object,
  featured_categories: Array
})

const loading = ref(true)
const products = ref([])
const windowWidth = ref(window.innerWidth)

// update window width on resize
const updateWidth = () => {
  windowWidth.value = window.innerWidth
}
onMounted(() => window.addEventListener('resize', updateWidth))
onUnmounted(() => window.removeEventListener('resize', updateWidth))

// Swiper breakpoints
const breakpoints = {
  1500: {slidesPerView: 4},
  880:  {slidesPerView: 3},
  750:  {slidesPerView: 2},
  380:  {slidesPerView: 2},
  0:    {slidesPerView: 2}
}

// compute slidesPerView based on current width
const slidesPerView = computed(() => {
  const points = Object.keys(breakpoints)
      .map(n => parseInt(n)).sort((a, b) => a - b)
  let count = 4
  for (const w of points) {
    if (windowWidth.value >= w) {
      count = breakpoints[w].slidesPerView
    }
  }
  return count
})

// placeholder skeleton count = slidesPerView
const placeholderCount = computed(() => slidesPerView.value)

// fetch slider data
onMounted(async () => {
  try {
    const {data} = await axios.get(`/api/slider/${props.type}`)
    products.value = data.slider?.products || []
  } catch (e) {
    console.error('Failed to fetch slider data', e)
  } finally {
    loading.value = false
  }
})
</script>